import ExcelJS from 'exceljs';

const HEADER_FILL = '1E3A5F';
const AVG_FILL = '0F766E';
const RANK_FILL = '115E59';
const HUMANITIES_ODD = 'FFEDD5';
const HUMANITIES_EVEN = 'FED7AA';
const DUP_ODD = 'FECACA';
const DUP_EVEN = 'FCA5A5';
const FAIL_FILL = 'FECACA';
const FAIL_FONT = '7F1D1D';
const ALT_ROW = 'EEF5FB';

const FAIL_PCT = 33.3;

function fillSolid(argb) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${argb}` } };
}

function thinBorder() {
  const b = { style: 'thin', color: { argb: 'FFCBD5E1' } };
  return { top: b, left: b, bottom: b, right: b };
}

function headerFont() {
  return { bold: true, color: { argb: 'FFFFFFFF' }, size: 9, name: 'Calibri' };
}

function bodyFont(opts = {}) {
  return { size: 9, name: 'Calibri', ...opts };
}

function cellDisplay(v) {
  if (v == null || v === '') return '';
  return String(v);
}

function isHumanities(groupName) {
  return /human/i.test(String(groupName || ''));
}

function parseMark(value) {
  if (value == null) return null;
  const t = String(value).trim();
  if (!t || t === '--' || t === '-' || t === '—') return null;
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

function isFailCell(value, key, subjectMax) {
  const max = subjectMax[key];
  const mark = parseMark(value);
  if (max == null || mark == null || max <= 0) return false;
  return (mark / max) * 100 < FAIL_PCT;
}

function collectDupRolls(rows) {
  const counts = new Map();
  for (const r of rows) {
    const roll = String(r.roll_no || '').trim();
    if (!roll) continue;
    counts.set(roll, (counts.get(roll) || 0) + 1);
  }
  const dups = new Set();
  for (const [roll, n] of counts) {
    if (n > 1) dups.add(roll);
  }
  return dups;
}

function buildSheet(workbook, sheetName, { columns, rows, stats, subjectMax, meta, statsScopeLabel = 'included only' }) {
  const ws = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: rows.length ? 3 : 1 }]
  });

  const dups = collectDupRolls(rows);
  const colCount = columns.length;

  columns.forEach((c, i) => {
    const col = ws.getColumn(i + 1);
    if (c.key === 'student_name') col.width = 22;
    else if (c.key === 'roll_no') col.width = 10;
    else if (c.key === 'rank_no') col.width = 6;
    else if (c.key === 'percentage_label') col.width = 8;
    else if (c.label && c.label.length > 6) col.width = 10;
    else col.width = 8;
  });

  const headerRow = ws.addRow(columns.map((c) => c.label || ''));
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.fill = fillSolid(HEADER_FILL);
    cell.font = headerFont();
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = thinBorder();
  });

  if (rows.length > 0 || Object.keys(stats?.avgs || {}).length > 0) {
    const avgValues = columns.map((c) => {
      if (c.key === 'student_name') return `Subject avg % (${statsScopeLabel})`;
      if (c.key === 'rank_no' || c.key === 'roll_no') return '';
      const avg = stats.avgs[c.key];
      return avg == null ? '' : `${avg.toFixed(1)}%`;
    });
    const avgRow = ws.addRow(avgValues);
    avgRow.height = 16;
    avgRow.eachCell((cell) => {
      cell.fill = fillSolid(AVG_FILL);
      cell.font = headerFont();
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = thinBorder();
    });

    const rankValues = columns.map((c) => {
      if (c.key === 'student_name') return `Subject rank 9th/10th (${statsScopeLabel})`;
      if (c.key === 'rank_no' || c.key === 'roll_no') return '';
      const rank = stats.ranks[c.key];
      return rank == null ? '' : String(rank);
    });
    const rankRow = ws.addRow(rankValues);
    rankRow.height = 16;
    rankRow.eachCell((cell) => {
      cell.fill = fillSolid(RANK_FILL);
      cell.font = headerFont();
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = thinBorder();
    });
  }

  rows.forEach((r, idx) => {
    const humanities = isHumanities(r.group_name);
    const dup = dups.has(String(r.roll_no || '').trim());
    const values = columns.map((c) => cellDisplay(r[c.key]));
    const excelRow = ws.addRow(values);
    excelRow.height = 15;

    let rowFill = null;
    if (dup) rowFill = idx % 2 === 0 ? DUP_ODD : DUP_EVEN;
    else if (humanities) rowFill = idx % 2 === 0 ? HUMANITIES_ODD : HUMANITIES_EVEN;
    else if (idx % 2 === 1) rowFill = ALT_ROW;

    excelRow.eachCell((cell, colNumber) => {
      const col = columns[colNumber - 1];
      const fail = col && isFailCell(r[col.key], col.key, subjectMax);

      cell.border = thinBorder();
      cell.alignment = {
        horizontal: col?.key === 'student_name' ? 'left' : 'center',
        vertical: 'middle'
      };

      if (fail) {
        cell.fill = fillSolid(FAIL_FILL);
        cell.font = bodyFont({ bold: true, color: { argb: `FF${FAIL_FONT}` } });
      } else if (col?.key === 'marks_diff') {
        const raw = String(r.marks_diff || '').replace(/^\+/, '');
        const n = Number(raw);
        if (rowFill) cell.fill = fillSolid(rowFill);
        if (Number.isFinite(n) && n < 0) {
          cell.font = bodyFont({ bold: true, color: { argb: 'FFB91C1C' } });
        } else if (Number.isFinite(n) && n > 0) {
          cell.font = bodyFont({ bold: true, color: { argb: 'FF047857' } });
        } else {
          cell.font = bodyFont();
        }
      } else {
        if (rowFill) cell.fill = fillSolid(rowFill);
        cell.font = bodyFont();
      }
    });
  });

  ws.addRow([]);
  const legend = ws.addRow([
    `Exported: ${meta.level} · ${meta.part} · ${meta.year} · ${rows.length} student(s)`
  ]);
  legend.getCell(1).font = bodyFont({ italic: true, color: { argb: 'FF64748B' } });
  ws.mergeCells(legend.number, 1, legend.number, Math.min(6, colCount || 6));

  const legend2 = ws.addRow([
    'Colors: orange = Humanities · red row = duplicate roll · red cell = subject < 33.3%'
  ]);
  legend2.getCell(1).font = bodyFont({ italic: true, color: { argb: 'FF64748B' } });
  ws.mergeCells(legend2.number, 1, legend2.number, Math.min(8, colCount || 8));

  return ws;
}

/**
 * Download Board Results .xlsx with UI-matching colors / frozen header rows.
 */
export async function exportBoardResultsExcel({
  columns,
  activeRows,
  disabledRows = [],
  subjectStats,
  subjectMax,
  level,
  part,
  year,
  sectionName,
  statsScopeLabel = 'included only'
}) {
  const ExcelJSMod = ExcelJS?.Workbook ? ExcelJS : ExcelJS.default;
  const workbook = new ExcelJSMod.Workbook();
  workbook.creator = 'FastGrader';
  workbook.created = new Date();

  const sectionLabel = sectionName || 'Board Results';

  buildSheet(workbook, sectionLabel.slice(0, 31), {
    columns,
    rows: activeRows,
    stats: subjectStats,
    subjectMax,
    meta: { level, part, year },
    statsScopeLabel
  });

  if (disabledRows.length > 0) {
    buildSheet(workbook, 'Excluded', {
      columns,
      rows: disabledRows,
      stats: subjectStats,
      subjectMax,
      meta: { level, part, year },
      statsScopeLabel
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const filename = `Board_Results_${sectionLabel}_${level}_${part}_${year}.xlsx`.replace(/\s+/g, '_');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
